import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import TaskFormBack from "./task-form-back";
import TaskFormMain from "./task-form-main";
import "./task-form.scss";
import {
  SaveDynamicFormData,
  ImportData,
  GetDynamicFormAutomations,
} from "../../../../services/api/task-form-api";
import TaskFormComplete from "./task-form-complete";
import { Helmet } from "react-helmet-async";
import {
  CompareDataWithApiName,
  GetCurrentSubFormIndex,
  UpdateForm,
  UpdateSubForm,
  LoadFormData,
  processConditionsAndUpdateFields,
  handleCustomFormulaClickUtil,
  processInitialConditions,
  checkAndSaveInitialReviewPeriodDates,
  FieldTypes,
  preprocessAutomations,
  reapplyAllFieldInputAutomations,
  applyInitialAutomations,
  applyFormSubmissionAutomations,
} from "./task-form-util";
import PropTypes from "prop-types";
import TaskFormSkeleton from "./task-form-skeleton";
import { useFluentToast } from "../../../../components/FluentUI/Fluent-Toast";
import { LoginContext } from "../../../../services/api/authentication-api";

export default function TaskForm(props) {

  let params = useParams();
  const [formData, setFormData] = useState({
    data: null,
    isLoading: true,
    isCompleted: false,
    autoPopulateCount: 0,
  });
  const [selectedSubForm, setSelectedSubForm] = useState(null);
  const [conditionMap, setConditionMap] = useState({});
  const [formStatusMap, setFormStatusMap] = useState({});
  const [automationMap, setAutomationMap] = useState({
    fieldInputMap: {},
    formStatusAutomationMap: {},
    taskCreatedList: [],
    formSubmissionList: [],
  });
  const [submitClicked, setSubmitClicked] = useState(false);
  const [isView, setIsView] = useState(false);
  const { showToast, FluentToast } = useFluentToast();
  const userContext = useContext(LoginContext);
  const labels = userContext?.currentUser?.objectLabels;

  useEffect(() => {
    if (formData.autoPopulateCount !== -1) {
      // to not trigger when reset is clicked
      LoadForm();
    }
  }, [formData.autoPopulateCount]);

  useEffect(() => {
    if (formData.isSaved) {
      LoadForm();

    }
  }, [formData.isSaved]);

  useEffect(() => {
    if (formData.data?.Task?.StatusId === 2 || formData.data?.Task?.StatusId === 3 || formData.data?.Task?.StatusId === 8
        || formData.data?.Task?.isArchived === true) {
      setIsView(true);
    } else {
      setIsView(false);
    }
  }, [formData.data?.Task?.StatusId, formData.data?.Task?.isArchived]);

  const LoadForm = async () => {
    const { result, conditionMap, formStatusMap } = await LoadFormData(params, props);
    const updatedFormData = await processInitialConditions(result.data, conditionMap);

    // Fetch and apply automations
    let processedAutomationMap = {
      fieldInputMap: {},
      formStatusAutomationMap: {},
      taskCreatedList: [],
      formSubmissionList: [],
    };
    try {
      const taskTypeId = updatedFormData.Task?.WorkItemTypeId;
      if (taskTypeId) {
        const automationsResponse = await GetDynamicFormAutomations(
          taskTypeId,
          params.formId
        );
        processedAutomationMap = preprocessAutomations(automationsResponse.data);
        applyInitialAutomations(
          updatedFormData,
          processedAutomationMap,
          updatedFormData.StatusId
        );
      }
    } catch (error) {
      console.error("Error loading form automations:", error);
    }

    setFormData((prev) => ({
      ...prev,
      data: updatedFormData,
      isLoading: false,
      isSaved: false,
    }));
    setSelectedSubForm((prev) => ({
      ...prev,
      ...updatedFormData?.SubForms[
      GetCurrentSubFormIndex(updatedFormData, selectedSubForm)
      ],
    }));
    setConditionMap(conditionMap);
    setFormStatusMap(formStatusMap);
    setAutomationMap(processedAutomationMap);
  };

  const handleCustomFormulaButtonClick = async (field) => {
    await handleCustomFormulaClickUtil(field, formData, SetDataWithSameApiName);
  };

  const SetDataWithSameApiName = async (field, value) => {
    let { currentSubForm, form } = CompareDataWithApiName(formData, field, value, selectedSubForm.Id);
    const fieldId = field.DynamicFieldId.toString();
    if (conditionMap[fieldId]) {
      const conditions = conditionMap[fieldId];
      currentSubForm = await processConditionsAndUpdateFields(currentSubForm, conditions, value, formData);
    }

    // Re-evaluate ALL field input automations so overlapping targets
    // (sections/fields controlled by multiple trigger fields) stay correct.
    if (automationMap.fieldInputMap[fieldId]) {
      const automationResult = reapplyAllFieldInputAutomations(form, automationMap.fieldInputMap);
      currentSubForm = form.SubForms.find((sf) => sf.Id === selectedSubForm.Id) || currentSubForm;

      if (automationResult.statusChange) {
        setFormStatusMap((prev) => ({
          ...prev,
          StatusName: automationResult.statusChange.statusName,
        }));
      }
    }

    setFormData((prev) => ({
      ...prev,
      data: form,
    }));
    setSelectedSubForm((prev) => ({
      ...prev,
      ...currentSubForm,
    }));
  };

  const TabChange = (tabId) => {
    const subForm = formData.data?.SubForms.find((sf) => sf.Id === tabId);
    setSelectedSubForm((prev) => ({
      ...prev,
      ...subForm,
    }));
  };

  const AutoPopulateSubForm = async (setSubmitting) => {
    setSubmitting(true);
    await checkAndSaveInitialReviewPeriodDates(formData, params);
    const response = await ImportData(params.taskId);
    if (response.status === 200) {
      let count =
        formData.autoPopulateCount === -1 ? 1 : formData.autoPopulateCount + 1;
      setFormData((prev) => ({
        ...prev,
        isLoading: true,
        autoPopulateCount: count,
      }));
    }

    setSubmitting(false);
  };

  const ResetFormSection = (setSubmitting, values) => {
    setSubmitting(true);
    const subForm = UpdateSubForm(selectedSubForm, values);
    const { form } = UpdateForm(subForm, formData);

    setFormData((prev) => ({
      ...prev,
      data: form,
      autoPopulateCount: -1,
    }));
    setSelectedSubForm((prev) => ({ ...prev, ...subForm }));
    setSubmitting(false);
  };

  const SaveSubForm = async (setSubmitting, values, type) => {
    try {
      setSubmitting(true);
      const subForm = UpdateSubForm(selectedSubForm, values, false);
      let { form, subFormIndex } = UpdateForm(subForm, formData);

      if (type === "Next") subFormIndex += 1;
      else if (type === "Back") subFormIndex -= 1;
      else if (type === "Complete") {
        setSubmitClicked(true);
        let validate = true;
        let subformId = 0;
        form.SubForms.forEach((subForm, index) => {
          // Skip validation for sections hidden by automations
          if (subForm.IsVisible === false) return;
          subForm.Fields.forEach(field => {
            const isRequired = field.IsRequired || subForm.IsMandatory === true;
            if (isRequired && field.IsVisible && (field.FieldType === FieldTypes.file ? (!field.Files || field.Files.length === 0) : (!field.Data || String(field.Data).trim() === ""))) {
              validate = false;
              subformId = index;
            }
          });
        });
        if (!validate) {
          subFormIndex = subformId;
          type = "";
        }
        else {
          await SaveDynamicFormData({ ...form, TaskId: params.taskId, IsSubmit: true });

          // Apply form submission automations
          const submissionResult = applyFormSubmissionAutomations(form, automationMap.formSubmissionList);
          if (submissionResult.statusChange) {
            setFormStatusMap((prev) => ({
              ...prev,
              StatusName: submissionResult.statusChange.statusName,
            }));
          }
        }
      }
      else if (type === "FormSave" || type === "Save and Next") {
        if (type === "Save and Next") {
          subFormIndex += 1;
        }
        await SaveDynamicFormData({ ...form, TaskId: params.taskId, IsSubmit: false });
        const now = new Date();
        const formattedDate = now.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
        const formattedTime = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
        showToast(`${labels?.FormSingular ?? "form"} Saved`, `On ${formattedDate} at ${formattedTime} by ${userContext?.currentUser?.profile?.userNameFull}`);
      }

      setFormData((prev) => ({
        ...prev,
        data: form,
        isCompleted: type === "Complete",
        isSaved: type === "FormSave" || type === "Save and Next",
        isLoading: type === "FormSave" || type === "Save and Next",
      }));
      setSelectedSubForm((prev) => ({
        ...prev,
        ...formData.data?.SubForms[subFormIndex],
      }));
      setSubmitting(false);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className="container-fluid form">

      <Helmet>
        <title>{`TRIYO · ${formData?.data?.Label ?? labels?.FormSingular ?? "Form"}`}</title>
      </Helmet>
      {formData.isLoading ? (
        <TaskFormSkeleton />
      ) : (
        <div className="row m-0 h-100">
          <div className="d-flex flex-column w-100 form-row-child">
            {!props.IsView && <div className="form-back-panel">
              <TaskFormBack />
            </div>}

            <div className="p-0 form-content">
              {(formData.isCompleted) ? (
                <TaskFormComplete isSaved={formData.isSaved} FormData={formData.data} />
              ) : (
                <TaskFormMain
                  subFormLabels={formData.data?.SubForms
                    .filter((subForm) => subForm?.IsVisible !== false)
                    .map((subForm) => ({
                      Label: subForm?.Label,
                      Id: subForm?.Id,
                    }))}
                  currentSubForm={selectedSubForm}
                  SaveSubForm={SaveSubForm}
                  label={formData.data?.Label}
                  ResetFormSection={ResetFormSection}
                  TabChange={TabChange}
                  SetDataWithSameApiName={SetDataWithSameApiName}
                  AutoPopulateSubForm={AutoPopulateSubForm}
                  AutoPopulateCount={formData.autoPopulateCount}
                  handleCustomFormulaButtonClick={handleCustomFormulaButtonClick}
                  IsView={props.IsView || isView}
                  submitClicked={submitClicked}
                  taskId={params.taskId ?? formData?.data?.TaskId}
                  formStatusMap={formStatusMap}
                />
              )}
            </div>

          </div>
        </div>
      )}
      {FluentToast}
    </div>
  );
}

TaskForm.propTypes = {
  IsView: PropTypes.bool.isRequired,
  FormData: PropTypes.object.isRequired,
};